import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Briefcase,
  CalendarDays,
  Clock3,
  HeartHandshake,
  Lightbulb,
  Mail,
  MapPin,
  Network,
  Users,
} from "lucide-react";
import EventFlyer from "./components/EventFlyer";
import {
  businessNetworkEventPath,
  formatBusinessNetworkEventAddress,
  formatBusinessNetworkEventDate,
  formatBusinessNetworkEventTime,
  getPublishedBusinessNetworkHighlights,
} from "./businessNetworkEvents";
import useBusinessNetworkEvents from "./useBusinessNetworkEvents";

const audiences = [
  "Local businesses",
  "Entrepreneurs",
  "Professionals",
  "Developers",
  "Housing associations",
  "Community organisations",
  "Public-sector partners",
  "Employers and suppliers",
];

const expectations = [
  {
    icon: Users,
    title: "Build relationships",
    description: "Meet people working across the local economy and community.",
  },
  {
    icon: Lightbulb,
    title: "Discover opportunities",
    description: "Share useful information about local procurement, employment and skills.",
  },
  {
    icon: HeartHandshake,
    title: "Create partnerships",
    description: "Explore practical collaboration and social-value activity.",
  },
  {
    icon: Network,
    title: "Connect locally",
    description: "Help businesses and organisations connect with the local community.",
  },
];

function eventLocation(event) {
  return formatBusinessNetworkEventAddress(event, { includeVenue: true });
}

function PreviousEventCard({ event }) {
  const path = businessNetworkEventPath(event);
  return (
    <article className="min-w-0 overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-4">
      <EventFlyer event={event} compact showFallback={false} className="mb-4" />
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#ff9aa2]">Previous event</p>
      <h3 className="mt-2 break-words text-xl font-bold">{event.title}</h3>
      <p className="mt-2 text-sm text-white/70">{formatBusinessNetworkEventDate(event)}</p>
      {event.highlightSummary ? (
        <p className="mt-3 text-sm leading-6 text-white/70">{event.highlightSummary}</p>
      ) : null}
      <Link
        to={path}
        className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-xl border border-white/15 bg-white/10 px-4 py-2 text-sm font-bold text-white hover:bg-white/15"
      >
        View event <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </article>
  );
}

export default function BusinessNetworkHub() {
  const {
    nextEvent,
    futureEvents,
    previousEvents,
    loading,
    error,
    retry,
  } = useBusinessNetworkEvents();
  const publishedHighlights = useMemo(
    () =>
      previousEvents
        .map((event) => ({ event, highlights: getPublishedBusinessNetworkHighlights(event) }))
        .filter(({ highlights }) => highlights),
    [previousEvents]
  );
  const nextPath = nextEvent ? businessNetworkEventPath(nextEvent) : "";
  const registrationOpen = nextEvent?.registrationOpen === true;

  return (
    <div className="mx-auto max-w-6xl space-y-12 md:space-y-16">
      <section
        className="relative overflow-hidden rounded-3xl border border-[#ff7a84]/35 bg-gradient-to-br from-[#E63946]/25 via-white/5 to-cyan-500/10 px-5 py-10 shadow-2xl md:px-10 md:py-14"
        aria-labelledby="business-network-title"
      >
        <div className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full bg-[#E63946]/20 blur-3xl" />
        <div className="relative max-w-4xl">
          <p className="text-sm font-black uppercase tracking-[0.18em] text-[#ff9aa2]">
            Monthly local business programme
          </p>
          <h1 id="business-network-title" className="mt-3 text-4xl font-black tracking-tight md:text-6xl">
            Urban Unity Business Network
          </h1>
          <p className="mt-5 text-2xl font-bold">Connect locally. Grow together.</p>
          <p className="mt-4 max-w-3xl text-lg leading-8 text-white/80">
            A place for local businesses, entrepreneurs, professionals and organisations to build relationships, discover opportunities and find practical ways to collaborate with one another and the local community.
          </p>
          <p className="mt-5 max-w-3xl rounded-xl border border-white/10 bg-black/20 p-4 font-semibold text-white/85">
            Events take place on the last Wednesday of every month. Published event details appear here as soon as registration opens.
          </p>
          {nextEvent && registrationOpen ? (
            <Link
              to={`${nextPath}?source=business-network-hub`}
              className="mt-7 inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-xl bg-[#E63946] px-6 py-3 text-center text-lg font-black text-white transition hover:bg-[#d62f3d] sm:w-auto"
            >
              Register for the next event <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </Link>
          ) : (
            <a
              href="mailto:info@urbanunitynetwork.com?subject=Urban%20Unity%20Business%20Network"
              className="mt-7 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/10 px-5 py-3 font-bold text-white hover:bg-white/15 sm:w-auto"
            >
              Contact UUN <Mail className="h-5 w-5" aria-hidden="true" />
            </a>
          )}
        </div>
      </section>

      <section aria-labelledby="next-business-network-event-title">
        <div className="mb-6">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-[#ff8b94]">Next event</p>
          <h2 id="next-business-network-event-title" className="mt-2 text-3xl font-black md:text-4xl">
            Join the next Business Network evening
          </h2>
        </div>

        {loading ? (
          <div role="status" className="rounded-2xl border border-white/10 bg-white/5 p-6 text-white/70">
            Loading the next published event…
          </div>
        ) : error ? (
          <div role="alert" className="rounded-2xl border border-amber-300/25 bg-amber-300/10 p-5 text-amber-100">
            <p>{error}</p>
            <button
              type="button"
              onClick={retry}
              className="mt-4 min-h-11 rounded-xl border border-amber-100/30 bg-black/20 px-4 py-2 font-semibold"
            >
              Try again
            </button>
          </div>
        ) : nextEvent ? (
          <article className="grid gap-6 rounded-3xl border border-[#ff7a84]/30 bg-white/5 p-5 md:grid-cols-[minmax(0,1fr)_auto] md:items-center md:p-8">
            <div className="min-w-0">
              <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#ff9aa2]">
                {registrationOpen ? "Registration open · Free to attend" : "Upcoming event"}
              </p>
              <h3 className="mt-2 break-words text-3xl font-black md:text-4xl">{nextEvent.title}</h3>
              <div className="mt-5 grid gap-3 text-white/80 sm:grid-cols-2">
                <p className="flex items-start gap-2">
                  <CalendarDays className="mt-0.5 h-5 w-5 shrink-0 text-[#ff9aa2]" aria-hidden="true" />
                  {formatBusinessNetworkEventDate(nextEvent)}
                </p>
                <p className="flex items-start gap-2">
                  <Clock3 className="mt-0.5 h-5 w-5 shrink-0 text-[#ff9aa2]" aria-hidden="true" />
                  {formatBusinessNetworkEventTime(nextEvent)}
                </p>
                <p className="flex items-start gap-2 sm:col-span-2">
                  <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-[#ff9aa2]" aria-hidden="true" />
                  {eventLocation(nextEvent) || "Venue to be confirmed"}
                </p>
              </div>
            </div>
            <Link
              to={`${nextPath}?source=business-network-hub`}
              className={`inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-xl px-6 py-3 text-center text-lg font-black transition md:w-auto ${
                registrationOpen
                  ? "bg-[#E63946] text-white hover:bg-[#d62f3d]"
                  : "border border-white/20 bg-white/10 text-white hover:bg-white/15"
              }`}
            >
              {registrationOpen ? "Register free" : "View event"}
              <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </Link>
          </article>
        ) : (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
            <h3 className="text-xl font-bold">The next date will be published soon.</h3>
            <p className="mt-2 text-white/70">
              Business Network events usually take place on the last Wednesday of each month.
            </p>
          </div>
        )}
      </section>

      <section className="grid gap-8 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]" aria-labelledby="about-business-network-title">
        <div className="rounded-3xl border border-white/10 bg-white/5 p-6 md:p-8">
          <Briefcase className="h-8 w-8 text-[#ff8b94]" aria-hidden="true" />
          <h2 id="about-business-network-title" className="mt-4 text-3xl font-black">About the network</h2>
          <p className="mt-4 leading-7 text-white/75">
            The Urban Unity Business Network brings together people and organisations with a local connection who want to share knowledge, build useful relationships and understand opportunities across the Isle of Dogs and neighbouring Tower Hamlets communities.
          </p>
          <p className="mt-4 leading-7 text-white/75">
            The programme can help participants explore local procurement, employment and skills opportunities, partnership working and practical social-value activity. Outcomes depend on the people and opportunities involved; attending does not guarantee contracts, employment or funding.
          </p>
        </div>
        <div className="rounded-3xl border border-white/10 bg-black/20 p-6 md:p-8">
          <h2 className="text-2xl font-black">Who the network brings together</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
            {audiences.map((audience) => (
              <li key={audience} className="flex items-center gap-3 text-white/80">
                <span className="h-2 w-2 shrink-0 rounded-full bg-[#ff7a84]" aria-hidden="true" />
                {audience}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section aria-labelledby="business-network-expect-title">
        <p className="text-sm font-bold uppercase tracking-[0.18em] text-[#ff8b94]">What to expect</p>
        <h2 id="business-network-expect-title" className="mt-2 text-3xl font-black md:text-4xl">
          Useful local conversations and connections
        </h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {expectations.map(({ icon: Icon, title, description }) => (
            <article key={title} className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <Icon className="h-7 w-7 text-[#ff8b94]" aria-hidden="true" />
              <h3 className="mt-4 text-xl font-bold">{title}</h3>
              <p className="mt-2 text-sm leading-6 text-white/70">{description}</p>
            </article>
          ))}
        </div>
      </section>

      {futureEvents.length > 0 ? (
        <section aria-labelledby="business-network-future-title">
          <h2 id="business-network-future-title" className="text-3xl font-black">Future published dates</h2>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            {futureEvents.map((event) => (
              <Link
                key={event.id}
                to={businessNetworkEventPath(event)}
                className="rounded-2xl border border-white/10 bg-white/5 p-5 transition hover:border-[#ff7a84]/40 hover:bg-white/10"
              >
                <span className="font-bold">{event.title}</span>
                <span className="mt-2 block text-sm text-white/70">{formatBusinessNetworkEventDate(event)}</span>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      <section aria-labelledby="previous-business-network-events-title">
        <p className="text-sm font-bold uppercase tracking-[0.18em] text-[#ff8b94]">Previous events</p>
        <h2 id="previous-business-network-events-title" className="mt-2 text-3xl font-black md:text-4xl">
          The network so far
        </h2>
        {previousEvents.length > 0 ? (
          <div className="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {previousEvents.map((event) => <PreviousEventCard key={event.id} event={event} />)}
          </div>
        ) : loading ? (
          <p role="status" className="mt-5 text-white/70">Loading previous events…</p>
        ) : (
          <p className="mt-5 rounded-2xl border border-white/10 bg-white/5 p-5 text-white/70">
            Previous event information will appear here once published.
          </p>
        )}
      </section>

      {publishedHighlights.length > 0 ? (
        <section aria-labelledby="business-network-highlights-title">
          <h2 id="business-network-highlights-title" className="text-3xl font-black">Photos and highlights</h2>
          <div className="mt-6 space-y-8">
            {publishedHighlights.map(({ event, highlights }) => (
              <article key={event.id} className="rounded-3xl border border-white/10 bg-white/5 p-5 md:p-8">
                <h3 className="text-2xl font-bold">{event.title}</h3>
                {highlights.summary ? <p className="mt-3 max-w-3xl leading-7 text-white/70">{highlights.summary}</p> : null}
                {highlights.images.length > 0 ? (
                  <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {highlights.images.map((image) => (
                      <figure key={image.url} className="overflow-hidden rounded-2xl border border-white/10 bg-black/20">
                        <img src={image.url} alt={image.alt} loading="lazy" decoding="async" className="aspect-[4/3] w-full object-cover" />
                        {image.caption ? <figcaption className="p-3 text-sm text-white/65">{image.caption}</figcaption> : null}
                      </figure>
                    ))}
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        </section>
      ) : null}

      <section className="grid gap-5 rounded-3xl border border-[#E63946]/30 bg-[#E63946]/10 p-6 md:grid-cols-[1fr_auto] md:items-center md:p-8" aria-labelledby="business-network-contact-title">
        <div>
          <h2 id="business-network-contact-title" className="text-2xl font-black md:text-3xl">Contact Urban Unity Network</h2>
          <p className="mt-3 max-w-2xl leading-7 text-white/75">
            Get in touch if you want to ask about an event, share a relevant local opportunity or discuss taking part in the network.
          </p>
        </div>
        <a
          href="mailto:info@urbanunitynetwork.com?subject=Urban%20Unity%20Business%20Network"
          className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 font-bold text-slate-950 hover:bg-slate-100 md:w-auto"
        >
          Email UUN <Mail className="h-5 w-5" aria-hidden="true" />
        </a>
      </section>
    </div>
  );
}
